/**
 * Attach a `cc_back` query param to a deployed-app URL so the mobile
 * app-nav-bar script (injected server-side by the deployed-apps proxy
 * in services/router/shared/proxy.py) knows where to return the user
 * when they tap "Back to CC" inside their app on mobile.
 *
 * Pass-through (no modification) when:
 *   - URL isn't a /a/<...> proxy path
 *   - URL is cross-origin
 *   - cc_back is already present (don't overwrite a caller-set value)
 *   - Anything goes wrong parsing the URL
 *
 * Use this at every dashboard call site that navigates to a deployed
 * app — href= on anchors, window.open(), window.location.href =, etc.
 * Do NOT use on URLs being copied to the clipboard (clipboard URLs are
 * shared externally and should not carry the caller's dashboard path).
 */
export function withCcBack(targetUrl: string | null | undefined): string {
  if (!targetUrl) return targetUrl ?? ''
  try {
    const u = new URL(targetUrl, window.location.origin)
    if (u.origin !== window.location.origin) return targetUrl
    if (!u.pathname.startsWith('/a/')) return targetUrl
    if (u.searchParams.has('cc_back')) return targetUrl
    u.searchParams.set('cc_back', window.location.pathname + window.location.search)
    return u.toString()
  } catch {
    return targetUrl
  }
}

/**
 * True when the dashboard is running as an installed PWA on a mobile
 * viewport — the only context where the injected app-nav-bar renders
 * (display-mode: standalone, or iOS Safari's legacy navigator.standalone,
 * AND a phone-width viewport). Mirrors the gating in the proxy snippet
 * (services/router/shared/proxy.py). Fails closed (false) on any error.
 */
export function isMobileStandalonePwa(): boolean {
  try {
    const standalone =
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true
    const mobile = !!window.matchMedia && window.matchMedia('(max-width: 768px)').matches
    return Boolean(standalone && mobile)
  } catch {
    return false
  }
}

/**
 * Open a deployed-app URL the way the rest of the dashboard expects,
 * but route it so the mobile app-nav-bar can actually appear.
 *
 *   - Installed PWA on mobile → navigate same-window (window.location)
 *     with `cc_back` attached. A new tab would escape the standalone
 *     container into the system browser, where the bar can't render and
 *     the user already has the OS chrome. Same-window keeps them inside
 *     CC's PWA, which is exactly where the bar gives back Back/URL/Share.
 *   - Everywhere else (desktop, regular browser tabs) → new tab, the
 *     unchanged prior behavior.
 *
 * `cc_back` is only attached for same-origin /a/<...> URLs (see
 * withCcBack); anything else passes through untouched.
 */
export function openDeployedApp(targetUrl: string | null | undefined): void {
  if (!targetUrl) return
  const url = withCcBack(targetUrl)
  if (isMobileStandalonePwa()) {
    window.location.href = url
  } else {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}
